import type { FinalJob } from '../lambda/types';
import { generateJobUrl, sanitizeForUrl } from '../routing/generate-url';
import { resolveJobUrlTitle } from '../routing/job-url-input';
import { LOCALIZED_PATHS, BASE_URL } from '../../utils/constants/url-constants';

export type Locale = keyof typeof LOCALIZED_PATHS;

export interface CanonicalUrlData {
  canonicalUrl: string;
  alternates: Array<{ href: string; hrefLang: string }>;
  xDefaultUrl: string;
}

// Re-export so callers that previously imported sanitizeForUrl from here still work.
export { sanitizeForUrl };

/**
 * Extracts locale-specific title from a FinalJob, falling back through locales.
 */
function getLocalizedTitle(job: FinalJob, locale: Locale): string {
  const genericJob = job as FinalJob & {
    title?: string;
    normalisedTitle?: string;
  };
  const localeTitle = resolveJobUrlTitle({
    normalisedTitle: job[`normalisedTitle_${locale}`],
    title: job[`title_${locale}`],
  });
  if (localeTitle) return localeTitle;

  const genericTitle = resolveJobUrlTitle({
    normalisedTitle: genericJob.normalisedTitle,
    title: genericJob.title,
  });
  if (genericTitle) return genericTitle;

  const englishTitle = resolveJobUrlTitle({
    normalisedTitle: job.normalisedTitle_en,
    title: job.title_en,
  });
  if (englishTitle) return englishTitle;

  const finnishTitle = resolveJobUrlTitle({
    normalisedTitle: job.normalisedTitle_fi,
    title: job.title_fi,
  });
  if (finnishTitle) return finnishTitle;

  return resolveJobUrlTitle({
    normalisedTitle: job.normalisedTitle_sv,
    title: job.title_sv,
  });
}

/**
 * Extracts locale-specific first location from a FinalJob, falling back through locales.
 */
function getLocalizedLocation(job: FinalJob, locale: Locale): string {
  const genericJob = job as FinalJob & {
    location?: string | string[];
  };
  const genericLocation = genericJob.location;
  const normalizedGenericLocation = Array.isArray(genericLocation)
    ? genericLocation[0] || ''
    : genericLocation || '';
  return (
    job[`location_${locale}`]?.[0] ||
    normalizedGenericLocation ||
    job.location_en?.[0] ||
    job.location_fi?.[0] ||
    job.location_sv?.[0] ||
    ''
  );
}

/**
 * Extracts locale-specific remoteness from a FinalJob, falling back through locales.
 */
function getLocalizedRemoteness(job: FinalJob, locale: Locale): string {
  const genericJob = job as FinalJob & {
    remoteness?: string;
  };
  return (
    job[`remoteness_${locale}`] ||
    genericJob.remoteness ||
    job.remoteness_en ||
    job.remoteness_fi ||
    job.remoteness_sv ||
    ''
  );
}

/**
 * Builds the absolute job URL for a given locale using the canonical generateJobUrl function.
 * This is the single source of truth shared with the SRP and JDP link generation.
 */
function buildUrl(job: FinalJob, locale: Locale): string {
  const genericJob = job as FinalJob & {
    companyName?: string;
    registryMainName?: string;
    sk?: string;
    pk?: string;
  };
  const jobId = (job.objectID || genericJob.pk || '').toString().trim();
  if (!jobId) {
    throw new Error('buildUrl: job.objectID or pk is required');
  }
  const companySegment =
    (typeof job.companyId === 'string' && job.companyId.trim() ? job.companyId : '') ||
    (typeof genericJob.companyName === 'string' && genericJob.companyName.trim() ? genericJob.companyName : '') ||
    (typeof genericJob.registryMainName === 'string' && genericJob.registryMainName.trim()
      ? genericJob.registryMainName
      : '') ||
    (typeof genericJob.sk === 'string' && genericJob.sk.trim() ? genericJob.sk : '');
  return generateJobUrl({
    baseUrl: BASE_URL,
    jobId,
    locale,
    company: companySegment,
    title: getLocalizedTitle(job, locale),
    location: getLocalizedLocation(job, locale),
    remoteness: getLocalizedRemoteness(job, locale),
  });
}

/**
 * Generates canonical and alternate URLs based on job data from the database.
 * Delegates to generateJobUrl so the URL structure is identical to the SRP and JDP.
 */
export function generateCanonicalUrls(job: FinalJob, currentLocale: Locale): CanonicalUrlData {
  const supportedLocales: Locale[] = ['en', 'fi', 'sv'];

  const canonicalUrl = buildUrl(job, currentLocale);

  const alternates = supportedLocales
    .filter(locale => locale !== currentLocale)
    .map(locale => ({
      href: buildUrl(job, locale),
      hrefLang: locale,
    }));

  const xDefaultUrl = buildUrl(job, 'en');

  return { canonicalUrl, alternates, xDefaultUrl };
}

export function generateJobUrlForLocale(job: FinalJob, locale: Locale): string {
  return buildUrl(job, locale);
}

/**
 * Extracts job ID and SEO slug from a job URL.
 */
export function parseJobUrl(url: string): { jobId: string; seoSlug?: string; locale?: Locale } | null {
  const patterns: Array<[RegExp, Locale]> = [
    [/\/en\/job\/([^\/]+)(?:\/(.+))?$/, 'en'],
    [/\/fi\/tyopaikka\/([^\/]+)(?:\/(.+))?$/, 'fi'],
    [/\/sv\/jobb\/([^\/]+)(?:\/(.+))?$/, 'sv'],
  ];

  for (const [pattern, locale] of patterns) {
    const match = url.match(pattern);
    if (match) {
      return { jobId: match[1], seoSlug: match[2], locale };
    }
  }

  return null;
}

/**
 * Validates if a job has sufficient data for URL generation.
 */
export function hasValidJobData(job: FinalJob): boolean {
  return !!(
    job.objectID &&
    (job.title_en || job.title_fi || job.title_sv ||
     job.normalisedTitle_en || job.normalisedTitle_fi || job.normalisedTitle_sv)
  );
}
